import { supabase } from "@internal/auth";
import { reportGlobalError } from "@internal/error-handling";
import { BehaviorSubject } from "rxjs";
import { VAPID_PUBLIC_KEY } from "@/lib/pushConfig";
import { arrayBufferToBase64Url, urlBase64ToUint8Array } from "@/lib/pushKey";

export type PermissionStatus = "default" | "granted" | "denied" | "unsupported";

interface PushSubscriptionState {
  permission: PermissionStatus;
  subscribed: boolean;
  busy: boolean;
}

const state$ = new BehaviorSubject<PushSubscriptionState>({
  permission: "default",
  subscribed: false,
  busy: false,
});

function detectPermission(): PermissionStatus {
  if (typeof window === "undefined") return "unsupported";
  if (!("Notification" in window)) return "unsupported";
  if (!("serviceWorker" in navigator)) return "unsupported";
  if (!("PushManager" in window)) return "unsupported";
  return Notification.permission;
}

async function readExistingSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

async function refreshState(): Promise<void> {
  const permission = detectPermission();
  if (permission === "unsupported" || permission !== "granted") {
    state$.next({ permission, subscribed: false, busy: false });
    return;
  }
  const existing = await readExistingSubscription();
  state$.next({ permission, subscribed: existing !== null, busy: false });
}

void refreshState().catch(reportGlobalError);

async function persistSubscription(
  userId: string,
  subscription: PushSubscription,
): Promise<void> {
  const json = subscription.toJSON();
  const endpoint = subscription.endpoint;
  const p256dh =
    json.keys?.p256dh ?? arrayBufferToBase64Url(subscription.getKey("p256dh"));
  const auth =
    json.keys?.auth ?? arrayBufferToBase64Url(subscription.getKey("auth"));

  const { error } = await supabase.from("push_subscriptions").upsert(
    {
      user_id: userId,
      endpoint,
      p256dh,
      auth,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      last_seen: new Date().toISOString(),
    },
    { onConflict: "endpoint" },
  );
  if (error) throw error;
}

async function deleteSubscriptionRow(endpoint: string): Promise<void> {
  const { error } = await supabase
    .from("push_subscriptions")
    .delete()
    .eq("endpoint", endpoint);
  if (error) throw error;
}

export const pushSubscriptionsService = {
  state$: state$.asObservable(),

  get snapshot(): PushSubscriptionState {
    return state$.getValue();
  },

  isSupported(): boolean {
    return detectPermission() !== "unsupported";
  },

  async refresh(): Promise<void> {
    await refreshState();
  },

  async enable(userId: string): Promise<void> {
    if (state$.getValue().busy) return;
    const supported = detectPermission();
    if (supported === "unsupported") return;
    if (!VAPID_PUBLIC_KEY) {
      throw new Error(
        "VAPID public key is missing — set VAPID_PUBLIC_KEY in src/lib/pushConfig.ts",
      );
    }
    state$.next({ ...state$.getValue(), busy: true });
    try {
      const permission =
        supported === "granted"
          ? "granted"
          : await Notification.requestPermission();
      if (permission !== "granted") {
        state$.next({
          permission: permission as PermissionStatus,
          subscribed: false,
          busy: false,
        });
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }
      await persistSubscription(userId, subscription);
      state$.next({ permission: "granted", subscribed: true, busy: false });
    } catch (err) {
      state$.next({ ...state$.getValue(), busy: false });
      throw err;
    }
  },

  async disable(): Promise<void> {
    if (state$.getValue().busy) return;
    state$.next({ ...state$.getValue(), busy: true });
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        await deleteSubscriptionRow(endpoint);
      }
      state$.next({
        permission: detectPermission(),
        subscribed: false,
        busy: false,
      });
    } catch (err) {
      state$.next({ ...state$.getValue(), busy: false });
      throw err;
    }
  },

  async sendTestNotification(): Promise<void> {
    const { error } = await supabase.functions.invoke("send-notifications", {
      body: { mode: "test" },
    });
    if (error) throw error;
  },
};
